import {
  type HTTPContentTypeCategory,
  contentTypeCategoryToCodeLanguage,
  getContentType,
  getContentTypeCategory,
} from '@carboncopyco/editor-utils';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { MonacoEditor } from '../../CodeEditor';
import { CodeInput, type CodeInputProps } from '../CodeInput';
import { ErrorMessage, FormField } from '../FormField';
import { ImageInput } from '../ImageInput';
import { getError } from '../errors';
import { responseSchema } from '../schema';

export interface StaticResponseFieldProps
  extends Pick<CodeInputProps, 'height'> {
  onError?: (messages: string[]) => void;
  hideLabel?: boolean;
  onFormat?: (value: string) => void;
  staticResponseCategory?: HTTPContentTypeCategory;
}

const fieldName = 'response';
const fieldLabel = responseSchema.describe().label;
const responseTypeFieldName = 'responseType';
const headersFieldName = 'headers';

export const StaticResponseField = ({
  onError,
  onFormat,
  height = 300,
  hideLabel,
  staticResponseCategory,
}: StaticResponseFieldProps) => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const editorRef = useRef<MonacoEditor>();
  const [errorMessage, setErrorMessage] = useState(null);
  const response = watch(fieldName);
  const responseType = watch(responseTypeFieldName);
  const headers = watch(headersFieldName);
  const contentType = getContentType(headers ?? []);
  const contentTypeCategory =
    staticResponseCategory ?? getContentTypeCategory(contentType);

  const codeLanguage =
    contentTypeCategoryToCodeLanguage[contentTypeCategory] ?? 'plaintext';

  const [formattedResponse, setFormattedResponse] = useState(response);

  useEffect(() => {
    if (staticResponseCategory && !response?.length) {
      setFormattedResponse(response);
    }
  }, [staticResponseCategory, response]);

  const changeImage = (url: string) => {
    const [contentType, response] =
      url.split('data:')?.[1]?.split(';base64,') ?? [];
    setValue(fieldName, response, { shouldDirty: true });
    setValue(headersFieldName, [
      ...headers.filter(
        (header) => header.name.toLowerCase() !== 'content-type'
      ),
      {
        name: 'content-type',
        value: contentType,
      },
    ]);
    trigger();
    onError?.([]);
  };

  useEffect(() => {
    register(fieldName);
    register(headersFieldName);
  }, []);

  useEffect(() => {
    setTimeout(
      () =>
        editorRef.current
          ?.getAction('editor.action.formatDocument')
          ?.run()
          .then(() => {
            onFormat?.(editorRef.current?.getValue());
            editorRef.current?.revealLine(1);
          }),
      500
    );
  }, [responseType]);

  return (
    <FormField
      name={fieldName}
      label={!hideLabel && fieldLabel}
    >
      {contentTypeCategory === 'image' ? (
        <ImageInput
          url={`data:${contentType};base64,${response}`}
          size={Number(height) - 32}
          state={errors[fieldName] || errorMessage ? 'error' : 'normal'}
          onChange={({ url }) => changeImage(url)}
          onError={(errorMessage) => onError?.([errorMessage])}
        />
      ) : (
        <CodeInput
          onMount={(editor) => {
            if (codeLanguage === 'json') {
              editor.getAction('editor.action.formatDocument')?.run();

              editorRef.current = editor;

              editorRef.current?.focus();
            }
          }}
          onChange={(response) => {
            setFormattedResponse(response);

            // If the response is JSON, try to un-format it, so it would match the unformatted default response.
            let value = response;
            if (codeLanguage === 'json') {
              try {
                value = JSON.stringify(JSON.parse(response));
              } catch {}
            }
            setValue(fieldName, value, {
              shouldDirty: true,
            });
            trigger(fieldName);
          }}
          onError={(errors) => {
            const errorMessages = errors.map(({ message }) => message);
            setErrorMessage(errorMessages.length ? errorMessages[0] : null);
            onError?.(errorMessages);
          }}
          value={formattedResponse}
          language={codeLanguage}
          height={height}
          state={errors[fieldName] || errorMessage ? 'error' : 'normal'}
        />
      )}
      <ErrorMessage
        errors={
          errorMessage
            ? {
                [fieldName]: {
                  message: errorMessage,
                  type: 'validation',
                },
              }
            : errors?.[fieldName]
              ? { [fieldName]: getError(errors[fieldName]) }
              : {}
        }
      />
    </FormField>
  );
};
